import { offset, shift, useClientPoint, useFloating, useInteractions } from '@floating-ui/react';
import { nanoid } from '@reduxjs/toolkit';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CollaborateTaskData } from "types/api/collaborate";
import { Input } from 'ui';
import { getSelectedSiteId } from '../../store/siteInfoSlice';
import { clearPendingTask, getTasks, setAnimateScreenshot, setCreateModalOpen, setLatestTaskCommentId, setPendingTask, setSelectedTask, setSelectedTaskId } from '../../store/tasksSlice';
import { getBrowseMode, getInputValue, getIsPageLoading, getShowTempMessage, setInputValue } from '../../store/uiSlice';

import { useAppDispatch, useAppSelector } from '../../store';
import { getShowForgotPassword, getShowLogin, getShowRegister } from '../../store/userSlice';
import { getNearestNode } from '../../utils/node';
import getUniqueSelector from '../../utils/selector';

function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.replace(/\/$/, '') || '/';
  } catch {
    return url;
  }
}

export const ElementSelector = () => {
  const dispatch = useAppDispatch();
  const browseMode = useAppSelector(getBrowseMode);
  const showForgot = useAppSelector(getShowForgotPassword);
  const showLogin = useAppSelector(getShowLogin);
  const showRegister = useAppSelector(getShowRegister);
  const siteId = useAppSelector(getSelectedSiteId);
  const tasks = useAppSelector(getTasks);
  const showTempMessage = useAppSelector(getShowTempMessage);
  const inputValue = useAppSelector(getInputValue);
  const isPageLoading = useAppSelector(getIsPageLoading);



  const selectedElementRef = useRef<HTMLElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(true);
  const { refs, floatingStyles, context } = useFloating({
    open: showTooltip,
    onOpenChange: setShowTooltip,
    middleware: [
      offset({
        mainAxis: 8,
        crossAxis: showTempMessage ? 100 : 80
      }),
      shift()
    ],
  });
  const clientPoint = useClientPoint(context);

  const { getFloatingProps } = useInteractions([
    clientPoint,
  ]);
  const mouseDownElementRef = useRef<HTMLElement | null>(null);

  const resetElementStyles = useCallback(() => {
    if (selectedElementRef.current) {
      selectedElementRef.current.style.cursor = '';
      selectedElementRef.current.style.removeProperty('outline');
      selectedElementRef.current.removeAttribute('data-atarim-selected');
      selectedElementRef.current = null;
    }
  }, []);

  const isAtarimUI = (e: HTMLElement) => {
    if (window.atarimUI) return true;
    while (e.parentNode) {
      if (e.id === 'root-atarim' || e.hasAttribute("data-atarim"))
        return true;
      e = e.parentNode as HTMLElement;
    }
    return false;
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (isAtarimUI(e.target as HTMLElement) || browseMode || window.__ATARIM_SCREENSHOT_MODE__) {
      return;
    }
    mouseDownElementRef.current = e.target as HTMLElement;
  };

  const handleClick = async (e: MouseEvent) => {
    if (isAtarimUI(e.target as HTMLElement) || browseMode || window.__ATARIM_SCREENSHOT_MODE__) return;

    e.preventDefault();
    e.stopPropagation();

    if (!mouseDownElementRef.current) return;

    const elementPath = getUniqueSelector(mouseDownElementRef.current, {
      useClasses: false
    });

    if (!elementPath) return;

    const mostRecentComment = (() => {
      if (tasks && tasks.length) {
        const maxId = tasks
          .filter(task => !task.task_id?.startsWith('PENDING'))
          .reduce((acc, cur) => cur.task_comment_id > acc ? cur.task_comment_id : acc, 0);
        return Math.max(maxId + 1, 1);
      }
      return 1;
    })();

    let existingTask: CollaborateTaskData | undefined;

    if (tasks) {
      // 1. Filter by page first (fast string compare)
      const normalizedCurrentUrl = normalizeUrl(window.location.href);
      const filteredTasks = tasks
        .filter(task => !task.task_id?.startsWith('PENDING') && !task.task_id?.startsWith('OPTIMISTIC'))
        .filter(task => 'task_type' in task)
        .filter(task => normalizeUrl(task.page_url) === normalizedCurrentUrl) as CollaborateTaskData[];

      // Cache the resolved element for the current click
      const element2 = await getNearestNode(elementPath, { maxRetries: 1, retryDelay: 0, fast: true });
      for (const task of filteredTasks) {
        const element1 = await getNearestNode(task.task_element_path, { maxRetries: 1, retryDelay: 0, fast: true });
        if (element1 === element2 && element1 !== null) {
          existingTask = task;
          break;
        }
      }
    }



    if (existingTask) {
      dispatch(setSelectedTaskId(existingTask.task_id));
      return;
    }

    const pendingTaskId = `PENDING-${nanoid(6)}`;
    dispatch(clearPendingTask());
    dispatch(
      setPendingTask({
        task_element_path: elementPath,
        task_comment_id: mostRecentComment,
        task_priority: 'low',
        task_status: 'open',
        task_id: pendingTaskId,
        is_internal: false,
        screenshot: '',
      })
    );
    dispatch(setLatestTaskCommentId(mostRecentComment));
    dispatch(setAnimateScreenshot(false));
    dispatch(setCreateModalOpen(pendingTaskId));
    dispatch(setSelectedTaskId(pendingTaskId));
    dispatch(setSelectedTask(undefined));
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isAtarimUI(e.target as HTMLElement) || browseMode) {
      setShowTooltip(false);
      return;
    }
  };

  const handleMouseOver = (e: MouseEvent) => {
    if (isAtarimUI(e.target as HTMLElement) || browseMode || window.__ATARIM_SCREENSHOT_MODE__) {
      setShowTooltip(false);
      return;
    }
    if (!e.target) return;

    if (selectedElementRef.current && selectedElementRef.current !== e.target) {
      resetElementStyles();
    }

    const targetElement = e.target as HTMLElement;
    selectedElementRef.current = targetElement;
    selectedElementRef.current.style.cursor = 'crosshair';
    selectedElementRef.current.style.setProperty('outline', '3px dashed red', 'important');
    selectedElementRef.current.setAttribute('data-atarim-selected', 'true');

    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    resetElementStyles();
    setShowTooltip(false);
  };

  useEffect(() => {
    if (!siteId) return

    try {
      document.querySelectorAll("a").forEach((link) => {
        try {
          const newUrl = new URL(link.href, `${window.location.protocol}//${window.location.hostname}`)
          newUrl.searchParams.set("atarimCollab", "true")
          if (window.ATARIM) newUrl.searchParams.set("siteId", siteId)
          link.href = newUrl.toString()
        } catch (linkError) {
          console.debug('Failed to update link:', linkError, link)
        }
      })
    } catch (error) {
      console.error('Error updating links for collaboration:', error)
    }
  }, [siteId])

  useEffect(() => {
    if (browseMode) {
      resetElementStyles();
      setShowTooltip(false);
      return;
    }

    setShowTooltip(true);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseLeave);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      resetElementStyles();
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseLeave);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [browseMode, tasks]);

  useEffect(() => {
    window.atarimUI = showForgot || showLogin || showRegister;
  }, [showForgot, showLogin, showRegister]);

  return (
    <div
      key='floating-element-selector'
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
      }}
      {...getFloatingProps()}
    >
      <AnimatePresence mode="wait">
        {showTempMessage && (
          <motion.div
            key="temp-message"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1, transition: { duration: 0.2 } }}
            exit={{ scale: 0.5, opacity: 0, transition: { duration: 0.1 } }}
            data-atarim="true"
          >
            <Input
              autoFocus
              type="text"
              autoComplete="off"
              value={inputValue}
              onChange={e => dispatch(setInputValue(e.target.value))}
              placeholder="Say something..."
              className="bg-ata-grey opacity-90 z-[100] w-full rounded-xl rounded-tl-[3px] focus-visible:ring-0 focus-visible:ring-offset-0 ring-offset-0 border-none text-white placeholder:text-white focus:ring-0 focus:outline-none"
            />
          </motion.div>
        )}
        {showTooltip && !showTempMessage && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1, transition: { duration: 0.2 } }}
            exit={{ scale: 0.5, opacity: 0, transition: { duration: 0.1 } }}
            className='bg-ata-grey z-[100] rounded-xl rounded-tl-[3px] text-white font-medium pointer-events-none shadow-sm px-2 py-1 text-xs'
            data-atarim="true"
          >
            <AnimatePresence mode="wait">
              {isPageLoading ? (
                <motion.span
                  key="loading"
                  initial={{ opacity: 0, scale: 0.95, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
                  exit={{ opacity: 0, scale: 0.95, y: -8, transition: { duration: 0.18, ease: 'easeIn' } }}
                  style={{ display: 'inline-block' }}
                >
                  Hang tight, setting things up
                </motion.span>
              ) : (
                <motion.span
                  key="comment"
                  initial={{ opacity: 0, scale: 0.95, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
                  exit={{ opacity: 0, scale: 0.95, y: -8, transition: { duration: 0.18, ease: 'easeIn' } }}
                  style={{ display: 'inline-block' }}
                >
                  Click to add a comment
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

declare global {
  interface Window {
    atarimUI?: boolean;
    __ATARIM_SCREENSHOT_MODE__?: boolean;
  }
}